import { useState } from "react";
import { Bot, Mail, MessageSquareText, Phone, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { Panel } from "@/components/admin/common";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { FollowUpInputs, Pick } from "./LeadFields";

const TYPES = [
  { value: "note", label: "Note", icon: MessageSquareText }, { value: "call", label: "Call", icon: Phone },
  { value: "whatsapp", label: "WhatsApp", icon: WhatsAppIcon }, { value: "email", label: "Email", icon: Mail },
  { value: "meeting", label: "Meeting", icon: Users },
];
const ICON = { ...Object.fromEntries(TYPES.map((t) => [t.value, t.icon])), system: Bot };
const when = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const AddActivity = ({ lead, onAdded }) => {
  const [f, setF] = useState({ type: "call", text: "", follow_up_date: "", follow_up_time: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { type: f.type, text: f.text, ...(f.follow_up_date ? { follow_up_date: f.follow_up_date, follow_up_time: f.follow_up_time } : {}) };
      const { data } = await api.post(`/admin/enquiries/${lead.id}/activities`, body);
      onAdded(data);
      setF({ type: f.type, text: "", follow_up_date: "", follow_up_time: "" });
      toast.success("Added to history");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 p-4" data-testid="activity-form">
      <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
        <Pick label="Type" value={f.type} onChange={set("type")} options={TYPES} testId="activity-type-select" />
        <FollowUpInputs f={f} set={set} prefix="activity-next" />
      </div>
      <Textarea required rows={3} maxLength={2000} value={f.text} onChange={(e) => set("text")(e.target.value)} placeholder="What was discussed? e.g. Called, wants Havelock sea-view room, will confirm by Friday" data-testid="activity-text-input" />
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">Set a date above to schedule the next follow-up.</p>
        <Button type="submit" disabled={saving} className="bg-ocean hover:bg-navy" data-testid="activity-submit-button">Add to history</Button>
      </div>
    </form>
  );
};

export const LeadTimeline = ({ lead, onAdded }) => {
  const items = [...(lead.activities || [])].reverse();
  return (
    <Panel className="p-5" data-testid="lead-timeline">
      <p className="font-semibold text-navy">Notes & conversation history</p>
      <div className="mt-4"><AddActivity lead={lead} onAdded={onAdded} /></div>
      <ol className="mt-5 space-y-4">
        {items.length === 0 && <p className="text-sm text-slate-500">No history yet.</p>}
        {items.map((a) => {
          const Icon = ICON[a.type] || Bot;
          return (
            <li key={a.id} className="flex gap-3" data-testid={`activity-${a.id}`}>
              <span className={`h-8 w-8 shrink-0 rounded-full grid place-items-center ${a.type === "system" ? "bg-slate-100 text-slate-500" : "bg-sky-50 text-ocean"}`}><Icon className="h-4 w-4" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-500"><span className="font-semibold capitalize text-slate-700">{a.type === "system" ? "Update" : a.type}</span> · {when(a.at)}{a.by && a.by !== "system" ? ` · ${a.by}` : ""}</p>
                <p className="mt-0.5 text-sm text-slate-700 whitespace-pre-line break-words">{a.text}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </Panel>
  );
};
